import { createRestSlice } from "./restSlice";

export type ReleaseStatus = "pending" | "released" | "failed";

/**
 * Runtime provenance recorded for dedicated (coderunner) releases — the
 * primfunctions version baked into the release's image. `image` is only
 * present for global admins; the API strips it for org users.
 */
export type ReleaseRuntime = {
  primfunctionsVersion?: string;
  image?: string;
  source: "coderunner-build";
  resolvedAt: string;
};

/** Coderunner build outcome snapshot (dedicated releases only). */
export type ReleaseBuild = {
  phase: string;
  message?: string | null;
  image?: string | null;
  readyReplicas?: number;
  desiredReplicas?: number;
  updatedAt: string;
};

/**
 * Live pod summary computed server-side from the coderunner control plane's
 * state snapshots (served as `runtimeStatus`; `runtime` is provenance).
 * Absent means "no runtime data" (not dedicated, or nothing observed yet) —
 * never render that as unhealthy.
 */
export type ReleaseRuntimeStatus = {
  podsReady: number;
  podsTotal: number;
  expectedReplicas: number;
  lastObservedAt: string | null;
  isStale: boolean;
};

/**
 * Derived operational status for dedicated releases; undefined for
 * shared-sandbox ones. `standby` is deliberate (stopped by an operator),
 * `degraded` means pods should be running but show failure signals.
 */
export type ReleaseOperationalStatus = "created" | "building" | "starting" | "running" | "degraded" | "stopping" | "standby" | "failed";

export type Release = {
  id: string;
  organizationId: string;
  agentId: string;
  environmentId: string;
  functionId?: string;
  // Creator attribution: userId for user-created releases, serviceAccountId
  // for releases created by an organization service account.
  userId: string | null;
  serviceAccountId: string | null;
  status: ReleaseStatus;
  runtime?: ReleaseRuntime;
  build?: ReleaseBuild;
  runtimeStatus?: ReleaseRuntimeStatus;
  operationalStatus?: ReleaseOperationalStatus;
  desiredState?: "running" | "standby";
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

const releaseSlice = createRestSlice<Release>("release", "/v1/releases");

export const releaseReducer = releaseSlice.reducer;
export const releaseActions = releaseSlice.actions;
